import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import RTLWrapper from "../components/RTLWrapper";
import { useUser } from "@clerk/clerk-react";
import { getUserProgress, updateLessonProgress } from "../data/database";
import SaveButton from "../components/SaveButton";

const numbers = [
  { word: "صِفْر", pronunciation: "ṣifr", meaning: "Zero", number: "0" },
  { word: "وَاحِد", pronunciation: "wāḥid", meaning: "One", number: "1" },
  { word: "اِثْنَان", pronunciation: "ithnān", meaning: "Two", number: "2" },
  { word: "ثَلَاثَة", pronunciation: "thalāthah", meaning: "Three", number: "3" },
  { word: "أَرْبَعَة", pronunciation: "arbaʿah", meaning: "Four", number: "4" },
  { word: "خَمْسَة", pronunciation: "khamsah", meaning: "Five", number: "5" },
  { word: "سِتَّة", pronunciation: "sittah", meaning: "Six", number: "6" },
  { word: "سَبْعَة", pronunciation: "sabʿah", meaning: "Seven", number: "7" },
  { word: "ثَمَانِيَة", pronunciation: "thamāniyah", meaning: "Eight", number: "8" },
  { word: "تِسْعَة", pronunciation: "tisʿah", meaning: "Nine", number: "9" },
  { word: "عَشَرَة", pronunciation: "ʿasharah", meaning: "Ten", number: "10" },
  { word: "عِشْرُونَ", pronunciation: "ʿishrūn", meaning: "Twenty", number: "20" },
  { word: "مِائَة", pronunciation: "miʾah", meaning: "Hundred", number: "100" },
  { word: "أَلْف", pronunciation: "alf", meaning: "Thousand", number: "1000" }
];

function NumbersLesson() {
  const { user } = useUser();
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [clickedNumbers, setClickedNumbers] = useState(new Set());

  // Load progress from database on mount
  useEffect(() => {
    const loadProgress = () => {
      if (user) {
        const userProgress = getUserProgress(user.id);
        const learnedCount = userProgress.numbers || 0;
        if (learnedCount > 0) {
          setClickedNumbers(new Set(numbers.slice(0, learnedCount).map(n => n.word)));
        }
      }
    };
    loadProgress();
  }, [user]);

  const handleClick = async (numberObj) => {
    setSelectedNumber(numberObj);
    const updated = new Set(clickedNumbers).add(numberObj.word);
    setClickedNumbers(updated);
    
    // Save progress to database
    if (user) {
      try {
        await updateLessonProgress(user.id, 'numbers', updated.size);
        console.log('Progress saved to database:', updated.size);
      } catch (error) {
        console.error('Error saving progress to database:', error);
      }
    }
    window.responsiveVoice.speak(numberObj.word, "Arabic Male");
  };

  const progress = (clickedNumbers.size / numbers.length) * 100;

  return (
    <RTLWrapper>
      <div className="p-4 sm:p-6 text-center h-full bg-gradient-to-br from-purple-50 to-white overflow-y-auto">
        <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-purple-800">الأرقام العربية</h2>

        {/* Progress Bar */}
        <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-4 mb-4 overflow-hidden shadow-inner">
          <motion.div
            className="bg-green-500 h-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <p className="text-sm text-gray-600 mb-6">تم تعلم {clickedNumbers.size} من {numbers.length} رقم</p>

        <AnimatePresence>
          {selectedNumber && (
            <motion.div
              key="number-info"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.3 }}
              className="text-lg sm:text-xl text-green-700 mb-6"
            >
              <p>النطق: <strong>{selectedNumber.pronunciation}</strong></p>
              <p>المعنى: <strong>{selectedNumber.meaning}</strong></p>
              <p>الرقم: <strong className="text-2xl">{selectedNumber.number}</strong></p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 justify-center mb-6">
          {numbers.map((numberObj, index) => {
            const learned = clickedNumbers.has(numberObj.word);
            return (
              <button
                key={index}
                onClick={() => handleClick(numberObj)}
                className={`text-xl font-semibold p-4 rounded-2xl shadow-md transition duration-200 text-wrap whitespace-pre-wrap break-words ${
                  learned ? "bg-green-100 text-green-700" : "bg-white text-purple-800 hover:bg-purple-100"
                }`}
              >
                <div className="text-3xl mb-2">{numberObj.number}</div>
                {numberObj.word}
              </button>
            );
          })}
        </div>

        {/* Save Button */}
        <div className="flex justify-center mb-6">
          <SaveButton
            lessonType="numbers"
            progressData={clickedNumbers.size}
          />
        </div>

        {/* ResponsiveVoice Script */}
        <script src="https://code.responsivevoice.org/responsivevoice.js?key=1X64heoV"></script>
      </div>
    </RTLWrapper>
  );
}

export default NumbersLesson; 